import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ShieldCheck, AlertTriangle, UserPlus, Search, X, Users, Gift, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGE_CHOICES = [
  { id: 'create',    label: 'Creative Mini Maker — $449' },
  { id: 'celebrate', label: 'Deluxe Fun Creator — $549' },
  { id: 'signature', label: 'Ultimate Artist — $699' },
];
const TIME_SLOT_CHOICES = [
  { v: '10:00', label: '10:00 AM' },
  { v: '13:00', label: '1:00 PM' },
  { v: '16:00', label: '4:00 PM' },
];

const empty = () => ({
  package_id: 'celebrate',
  date: '',
  time_slot: '10:00',
  guest_count: 8,
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  child_name: '',
  child_age: 8,
  add_ons: [],
  override_availability: false,
  send_email: true,
  internal_notes: '',
  pin: '',
});

export const ManualBookingDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(empty());
  const [submitting, setSubmitting] = useState(false);

  // ── Existing-customer autocomplete state
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custLoading, setCustLoading] = useState(false);
  const [linkedCustomer, setLinkedCustomer] = useState(null); // {email, name, total_bookings}
  const [recentBookings, setRecentBookings] = useState([]); // last 3
  const [recentLoading, setRecentLoading] = useState(false);
  // ── Loyalty redemption state (only meaningful for linked customers w/ account)
  const [loyaltyState, setLoyaltyState] = useState(null);   // {has_account, balance, rewards, all_rewards}
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [selectedRewardId, setSelectedRewardId] = useState(null);
  const searchTimer = useRef(null);
  const blurTimer = useRef(null);

  const reset = () => {
    setForm(empty());
    setCustQuery('');
    setCustResults([]);
    setCustOpen(false);
    setLinkedCustomer(null);
    setRecentBookings([]);
    setLoyaltyState(null);
    setSelectedRewardId(null);
  };

  // Debounced search (only when no customer is linked)
  useEffect(() => {
    if (!open) return;
    if (linkedCustomer) return;
    const q = custQuery.trim();
    if (q.length < 2) {
      setCustResults([]);
      setCustOpen(false);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setCustLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/customers`, {
          params: { search: q, sort_by: 'last_booking' },
          withCredentials: true,
        });
        setCustResults((res.data || []).slice(0, 8));
        setCustOpen(true);
      } catch (e) {
        // Silent — search is a nice-to-have
      } finally {
        setCustLoading(false);
      }
    }, 280);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [custQuery, open, linkedCustomer]);

  const pickCustomer = (c) => {
    // Prefer most recent valid child (skip empty entries from the aggregation)
    const children = (c.children || []).filter((ch) => ch && ch.name);
    const child = children[0] || {};
    setForm((f) => ({
      ...f,
      customer_name: c.name || f.customer_name,
      customer_email: c.email || f.customer_email,
      customer_phone: c.phone || f.customer_phone,
      child_name: child.name || f.child_name,
      child_age: child.age || f.child_age,
    }));
    setLinkedCustomer({ email: c.email, name: c.name, total_bookings: c.total_bookings, children });
    setCustOpen(false);
    setCustQuery('');
    toast.success(`Linked to ${c.name} (${c.total_bookings} prior booking${c.total_bookings === 1 ? '' : 's'})`);
    // Fire-and-fetch the last 3 bookings for context
    fetchRecentBookings(c.email);
    // Also fetch loyalty status for the redeem widget
    fetchLoyaltyStatus(c.email);
  };

  const fetchLoyaltyStatus = async (email) => {
    if (!email) return;
    setLoyaltyLoading(true);
    setLoyaltyState(null);
    setSelectedRewardId(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/loyalty/customer/${encodeURIComponent(email)}`, {
        withCredentials: true,
      });
      setLoyaltyState(res.data);
    } catch (e) {
      // Non-fatal
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const fetchRecentBookings = async (email) => {
    if (!email) return;
    setRecentLoading(true);
    setRecentBookings([]);
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers/${encodeURIComponent(email)}`, {
        withCredentials: true,
      });
      setRecentBookings((res.data?.bookings || []).slice(0, 3));
    } catch (e) {
      // Non-fatal — banner still useful without the mini-history
    } finally {
      setRecentLoading(false);
    }
  };

  const clearLink = () => {
    setLinkedCustomer(null);
    setRecentBookings([]);
    setLoyaltyState(null);
    setSelectedRewardId(null);
    setForm((f) => ({
      ...f,
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      child_name: '',
      child_age: 8,
    }));
  };

  const submit = async () => {
    const required = ['date', 'customer_name', 'customer_email', 'customer_phone', 'child_name'];
    for (const k of required) {
      if (!String(form[k] || '').trim()) {
        toast.error(`Missing: ${k.replace(/_/g, ' ')}`);
        return;
      }
    }
    if (!/^\d{4,6}$/.test(form.pin)) { toast.error('Enter your 4-digit PIN'); return; }
    if (parseInt(form.guest_count, 10) < 1) { toast.error('Guest count must be ≥ 1'); return; }

    setSubmitting(true);
    try {
      const payload = {
        package_id: form.package_id,
        date: form.date,
        time_slot: form.time_slot,
        guest_count: parseInt(form.guest_count, 10),
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim().toLowerCase(),
        customer_phone: form.customer_phone.trim(),
        child_name: form.child_name.trim(),
        child_age: parseInt(form.child_age, 10) || 0,
        add_ons: [],
        override_availability: form.override_availability,
        send_email: form.send_email,
        internal_notes: form.internal_notes.trim(),
        pin: form.pin,
        ...(selectedRewardId ? { apply_loyalty_reward_id: selectedRewardId } : {}),
      };
      const res = await axios.post(`${API_URL}/api/admin/manual-bookings`, payload, { withCredentials: true });
      const { booking_id, total, override_used, created_by_employee, loyalty_redemption } = res.data || {};
      const redemptionMsg = loyalty_redemption
        ? ` · Redeemed ${loyalty_redemption.points_cost}pts (${loyalty_redemption.reward_name})`
        : '';
      toast.success(
        `✅ Booked ${booking_id?.replace('booking_', '')} — total $${Number(total || 0).toFixed(2)} ` +
        `${override_used ? '(slot override used) ' : ''}` +
        `created by ${created_by_employee}${redemptionMsg}`
      );
      onOpenChange(false);
      reset();
      if (typeof onCreated === 'function') onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#FF3B6B]" />
            New Manual Booking
          </DialogTitle>
          <DialogDescription>
            Book on behalf of a phone-in or walk-in customer. The booking is marked as "pending payment" — you can collect payment later from CRM → Payments.
          </DialogDescription>
        </DialogHeader>

        {/* Existing customer autocomplete (above the form so the linked-banner sits at the top) */}
        <div className="mt-2" data-testid="mb-existing-customer">
          {linkedCustomer ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[#06D6A0]/40 bg-[#06D6A0]/10 px-3 py-2" data-testid="mb-linked-banner">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-[#058B69] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1E1E24] truncate">
                      Returning customer: {linkedCustomer.name}
                    </p>
                    <p className="text-[11px] text-[#058B69] truncate">
                      {linkedCustomer.email} · {linkedCustomer.total_bookings} prior booking{linkedCustomer.total_bookings === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearLink}
                  className="text-xs text-[#058B69] hover:text-[#1E1E24] underline shrink-0"
                  data-testid="mb-clear-link"
                >
                  Clear & enter new
                </button>
              </div>
              {/* Recent bookings mini-history */}
              {(recentLoading || recentBookings.length > 0) && (
                <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] px-3 py-2" data-testid="mb-recent-bookings">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A] font-semibold mb-1.5">
                    Recent bookings
                  </p>
                  {recentLoading ? (
                    <p className="text-xs text-[#6B6B7A]" data-testid="mb-recent-loading">Loading…</p>
                  ) : (
                    <ul className="space-y-1">
                      {recentBookings.map((b) => {
                        const status = (b.status || '').toLowerCase();
                        const paid = (b.payment_status || '').toLowerCase();
                        const isCancelled = status === 'cancelled';
                        const isPaid = paid === 'paid' || b.balance_paid;
                        const badgeClass = isCancelled
                          ? 'bg-[#FF3B6B]/15 text-[#FF3B6B]'
                          : isPaid
                            ? 'bg-[#06D6A0]/15 text-[#058B69]'
                            : 'bg-[#F59E0B]/15 text-[#B45309]';
                        const badgeLabel = isCancelled ? 'CANCELLED' : isPaid ? 'PAID' : (paid || status || 'pending').toUpperCase().replace(/_/g, ' ');
                        return (
                          <li key={b.booking_id}
                              className="flex items-center justify-between gap-2 text-xs"
                              data-testid={`mb-recent-row-${b.booking_id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-[10px] text-[#6B6B7A] shrink-0">{b.date}</span>
                              <span className="text-[#1E1E24] truncate">
                                {b.package_name || '—'} · {b.child_name || '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-[#1E1E24] font-semibold">${Number(b.total || 0).toFixed(0)}</span>
                              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${badgeClass}`}>
                                {badgeLabel}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
              {/* Loyalty Points Redemption (iter-87) */}
              {linkedCustomer && (loyaltyLoading || loyaltyState) && (
                <div
                  className="rounded-xl border border-[#FF3B6B]/30 bg-gradient-to-br from-[#FFE9EE] to-[#FFF4D6] px-3 py-2"
                  data-testid="mb-loyalty-redeem"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Gift className="w-3.5 h-3.5 text-[#FF3B6B]" />
                      <p className="text-[10px] uppercase tracking-wide text-[#FF3B6B] font-bold">
                        Loyalty Points
                      </p>
                    </div>
                    {loyaltyState?.has_account && (
                      <span className="text-xs font-bold text-[#FF3B6B]" data-testid="mb-loyalty-balance">
                        {loyaltyState.balance} pts available
                      </span>
                    )}
                  </div>
                  {loyaltyLoading ? (
                    <p className="text-xs text-[#6B6B7A]">Loading rewards…</p>
                  ) : !loyaltyState?.has_account ? (
                    <p className="text-[11px] text-[#6B6B7A]">
                      Customer hasn't created an account yet — they need one to earn / redeem points.
                    </p>
                  ) : (loyaltyState.rewards || []).length === 0 ? (
                    <p className="text-[11px] text-[#6B6B7A]">
                      Not enough points to redeem any reward yet (need at least {Math.min(...(loyaltyState.all_rewards || []).map(r => r.points_cost || 0).filter(c => c > 0), Infinity)} pts).
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {(loyaltyState.rewards || []).map((r) => {
                        const isSelected = selectedRewardId === r.reward_id;
                        return (
                          <button
                            key={r.reward_id}
                            type="button"
                            onClick={() => setSelectedRewardId(isSelected ? null : r.reward_id)}
                            className={`w-full text-left px-3 py-2 rounded-lg border transition flex items-center justify-between gap-2 ${
                              isSelected
                                ? 'bg-[#FF3B6B] text-white border-[#FF3B6B] shadow-sm'
                                : 'bg-white border-[#E8E5DF] hover:border-[#FF3B6B]'
                            }`}
                            data-testid={`mb-loyalty-reward-${r.reward_id}`}
                          >
                            <div className="min-w-0">
                              <p className={`text-xs font-semibold truncate ${isSelected ? 'text-white' : 'text-[#1E1E24]'}`}>
                                {r.name}
                              </p>
                              <p className={`text-[10px] truncate ${isSelected ? 'text-white/80' : 'text-[#6B6B7A]'}`}>
                                {r.description || (r.type === 'discount'
                                  ? (r.discount_type === 'percent'
                                      ? `${r.discount_value}% off`
                                      : `$${r.discount_value} off`)
                                  : r.type === 'freebie'
                                    ? 'Free add-on'
                                    : r.type)}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-[#FF3B6B]'}`}>
                                {r.points_cost} pts
                              </p>
                              {isSelected && (
                                <p className="text-[9px] text-white/90 mt-0.5 flex items-center justify-end gap-0.5">
                                  <Sparkles className="w-2.5 h-2.5" /> APPLIED
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                      {selectedRewardId && (
                        <p className="text-[10px] text-[#06D6A0] font-semibold mt-1" data-testid="mb-loyalty-applied">
                          ✓ Points will be deducted only after booking is confirmed
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <Label className="text-xs flex items-center gap-1 text-[#6B6B7A]">
                <Search className="w-3 h-3" /> Search existing customer (optional)
              </Label>
              <div className="relative mt-1">
                <Input
                  value={custQuery}
                  onChange={(e) => setCustQuery(e.target.value)}
                  onFocus={() => custResults.length > 0 && setCustOpen(true)}
                  onBlur={() => {
                    // Defer to allow click on a row
                    if (blurTimer.current) clearTimeout(blurTimer.current);
                    blurTimer.current = setTimeout(() => setCustOpen(false), 150);
                  }}
                  placeholder="Type name, email, or phone (min 2 chars)…"
                  className="pr-8 bg-white"
                  data-testid="mb-customer-search"
                  autoComplete="off"
                />
                {custQuery && (
                  <button
                    type="button"
                    onClick={() => { setCustQuery(''); setCustResults([]); setCustOpen(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B6B7A] hover:text-[#1E1E24]"
                    data-testid="mb-customer-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {custOpen && (
                <div
                  className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-xl border border-[#E8E5DF] shadow-lg max-h-72 overflow-y-auto"
                  data-testid="mb-customer-dropdown"
                >
                  {custLoading && (
                    <div className="px-3 py-2 text-xs text-[#6B6B7A]">Searching…</div>
                  )}
                  {!custLoading && custResults.length === 0 && (
                    <div className="px-3 py-2 text-xs text-[#6B6B7A]" data-testid="mb-customer-no-results">
                      No matches — fill the form below to create a new customer.
                    </div>
                  )}
                  {!custLoading && custResults.map((c) => (
                    <button
                      key={c.email}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#FFFDF8] border-b last:border-b-0 border-[#F1EFE9] transition"
                      data-testid={`mb-customer-option-${c.email}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#1E1E24] truncate">{c.name}</p>
                          <p className="text-[11px] text-[#6B6B7A] truncate">
                            {c.email}{c.phone ? ` · ${c.phone}` : ''}
                          </p>
                        </div>
                        <span className="text-[10px] text-[#FF3B6B] font-semibold whitespace-nowrap">
                          {c.total_bookings || 0} ×
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-[#6B6B7A] mt-1">Pick a result to auto-fill the customer & child fields.</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Package</Label>
            <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
              <SelectTrigger className="bg-white mt-1" data-testid="mb-package"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {PACKAGE_CHOICES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="mb-date" />
          </div>
          <div>
            <Label className="text-xs">Time slot</Label>
            <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
              <SelectTrigger className="bg-white mt-1" data-testid="mb-time"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {TIME_SLOT_CHOICES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Customer name *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1" data-testid="mb-customer-name" />
          </div>
          <div>
            <Label className="text-xs">Customer email *</Label>
            <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1" data-testid="mb-customer-email" />
          </div>
          <div>
            <Label className="text-xs">Customer phone *</Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1" data-testid="mb-customer-phone" />
          </div>
          <div>
            <Label className="text-xs">Guest count</Label>
            <Input type="number" min="1" max="20" value={form.guest_count}
              onChange={(e) => setForm({ ...form, guest_count: e.target.value })} className="mt-1" data-testid="mb-guests" />
          </div>
          <div>
            <Label className="text-xs">Child's name *</Label>
            <Input value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} className="mt-1" data-testid="mb-child-name" />
          </div>
          <div>
            <Label className="text-xs">Child's age</Label>
            <Input type="number" min="1" max="18" value={form.child_age}
              onChange={(e) => setForm({ ...form, child_age: e.target.value })} className="mt-1" data-testid="mb-child-age" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Internal notes (visible to staff only)</Label>
            <Textarea rows={2} value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
              placeholder="Phoned in 2pm; wants extra balloons; pay on arrival…"
              className="mt-1" data-testid="mb-notes" />
          </div>
        </div>

        {/* Override + email + PIN */}
        <div className="space-y-3 mt-3 pt-3 border-t border-[#E8E5DF]">
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={form.override_availability}
              onCheckedChange={(c) => setForm({ ...form, override_availability: !!c })}
              data-testid="mb-override"
            />
            <span>
              <span className="flex items-center gap-1 text-[#1E1E24] font-medium">
                <AlertTriangle className="w-4 h-4 text-[#F97316]" />
                Override availability (book even if slot is full)
              </span>
              <span className="block text-xs text-[#6B6B7A] mt-0.5">Use when the customer specifically requests an unavailable slot.</span>
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox checked={form.send_email} onCheckedChange={(c) => setForm({ ...form, send_email: !!c })} data-testid="mb-send-email" />
            <span>Send confirmation email to the customer</span>
          </label>
          <div>
            <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Your 4-digit staff PIN *</Label>
            <Input type="password" inputMode="numeric" maxLength={6} value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              className="mt-1 font-mono tracking-[0.4em] text-center text-lg" placeholder="••••" data-testid="mb-pin" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="mb-submit">
            {submitting ? 'Creating…' : 'Create booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualBookingDialog;
